import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { AlertTriangle, Star } from "lucide-react";

interface GuideUtilization {
  id: string;
  name: string;
  tripsCount: number;
  avgRating: number;
  revenue: number;
}

interface UnassignedTripDate {
  tripId: string;
  tripTitle: string;
  dateId: string;
  startDate: string;
}

interface CalendarData {
  guideId: string;
  guideName: string;
  dates: string[];
}

interface GuideStatsProps {
  utilization: GuideUtilization[];
  unassigned: UnassignedTripDate[];
  calendarData: CalendarData[];
}

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M Kč`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K Kč`;
  }
  return `${value.toLocaleString("cs-CZ")} Kč`;
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString("cs-CZ", {
    day: "numeric",
    month: "short",
  });
}

export default function GuideStats({ utilization, unassigned, calendarData }: GuideStatsProps) {
  const chartData = utilization.slice(0, 10).map((g) => ({
    name: g.name.split(" ")[0], // First name only for chart
    fullName: g.name,
    trips: g.tripsCount,
  }));

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Trips per Guide Chart */}
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Počet termínů podle průvodce
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis tick={{ fontSize: 12, fill: "#6b7280" }} />
                <Tooltip
                  formatter={(value: number, _, props) => [
                    `${value} termínů`,
                    props.payload.fullName,
                  ]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="trips" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Unassigned Warning */}
        <div className="card">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Termíny bez průvodce
            </h3>
          </div>
          <div className="max-h-56 space-y-2 overflow-auto">
            {unassigned.map((item) => (
              <Link
                key={item.dateId}
                to={`/trips/${item.tripId}`}
                className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3 transition-colors hover:bg-amber-100 dark:border-amber-900 dark:bg-amber-900/20 dark:hover:bg-amber-900/30"
              >
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {item.tripTitle}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formatDate(item.startDate)}
                  </p>
                </div>
              </Link>
            ))}
            {unassigned.length === 0 && (
              <p className="text-center text-green-600">
                Všechny termíny mají přiřazeného průvodce
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Guide Table */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Přehled průvodců
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Průvodce
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  Termíny
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  Hodnocení
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Tržby
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {utilization.map((guide) => (
                <tr key={guide.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="whitespace-nowrap px-4 py-3">
                    <Link
                      to={`/guides/${guide.id}`}
                      className="font-medium text-primary-600 hover:underline"
                    >
                      {guide.name}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-center">
                    <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {guide.tripsCount}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {guide.avgRating > 0 ? guide.avgRating.toFixed(1) : "–"}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(guide.revenue)}
                    </span>
                  </td>
                </tr>
              ))}
              {utilization.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    Žádná data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
